/* 3.74 — THE DEFINITION IS NOW GUARDED, AND THIS IS NOT DECORATION.

   `class QuickAddModal extends ModalDialog` is evaluated the moment this file
   parses. ModalDialog lives in global.js. Between 3.56 and 3.73 this file was
   emitted from meta-tags.liquid, which renders above global.js's tag, and
   deferred scripts execute in document order — so the extends clause hit a
   ReferenceError, the script died on line one, and <quick-add-modal> was never
   registered. modal-opener then called .show() on an un-upgraded element and
   threw. Every multi-variant card's choose-options popup was dead for months
   with nothing in the console unless you were looking at page load.

   3.74 also fixes the load order (see layout/theme.liquid), so this guard is
   belt and braces. It earns its place anyway: the failure it prevents is
   invisible, and the same trap is re-armed by any future tag move. Now a wrong
   order costs a retry at DOMContentLoaded — by which point every deferred
   script has run — and, failing that, one loud console warning instead of
   silence. */
(function () {
  function defineQuickAddModal() {
    if (customElements.get('quick-add-modal')) return true;
    if (typeof ModalDialog === 'undefined') return false;
    customElements.define(
      'quick-add-modal',
      class QuickAddModal extends ModalDialog {
        constructor() {
          super();
          this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

          this.addEventListener('product-info:loaded', ({ target }) => {
            target.addPreProcessCallback(this.preprocessHTML.bind(this));
          });
        }

        hide(preventFocus = false) {
          const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cartNotification) cartNotification.setActiveElement(this.openedBy);
          this.modalContent.innerHTML = '';

          if (preventFocus) this.openedBy = null;
          super.hide();
        }

        show(opener) {
          // 3.66: close the basket drawer first. The drawer sits at z-index 1000
          // and this modal at 101, so a quick add opened over an open drawer
          // rendered behind it with its right-hand column covered. The CSS in
          // quick-add-tinker.css now also raises the modal above the drawer, but
          // closing is the better behaviour: two stacked overlays is a muddled
          // state even when the stacking order is right, and a shopper opening a
          // quick add has plainly finished looking at the basket.
          // cart-drawer.close() already no-ops unless <html> carries .ccd-open,
          // so this is safe to call unconditionally.
          document.querySelector('cart-drawer')?.close?.();

          opener.setAttribute('aria-disabled', true);
          opener.classList.add('loading');
          opener.querySelector('.loading__spinner').classList.remove('hidden');

          fetch(opener.getAttribute('data-product-url'))
            .then((response) => response.text())
            .then((responseText) => {
              const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
              const productElement = responseHTML.querySelector('product-info');

              this.preprocessHTML(productElement);
              HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }
              if (window.ProductModel) window.ProductModel.loadShopifyXR();

              super.show(opener);
            })
            .finally(() => {
              opener.removeAttribute('aria-disabled');
              opener.classList.remove('loading');
              opener.querySelector('.loading__spinner').classList.add('hidden');
            });
        }

        preprocessHTML(productElement) {
          productElement.classList.forEach((classApplied) => {
            if (classApplied.startsWith('color-') || classApplied === 'gradient')
              this.modalContent.classList.add(classApplied);
          });
          this.preventDuplicatedIDs(productElement);
          this.removeDOMElements(productElement);
          this.removeGalleryListSemantic(productElement);
          this.updateImageSizes(productElement);
          this.preventVariantURLSwitching(productElement);
        }

        preventVariantURLSwitching(productElement) {
          productElement.setAttribute('data-update-url', 'false');
        }

        removeDOMElements(productElement) {
          const pickupAvailability = productElement.querySelector('pickup-availability');
          if (pickupAvailability) pickupAvailability.remove();

          const productModal = productElement.querySelector('product-modal');
          if (productModal) productModal.remove();

          const modalDialog = productElement.querySelectorAll('modal-dialog');
          if (modalDialog) modalDialog.forEach((modal) => modal.remove());
        }

        preventDuplicatedIDs(productElement) {
          const sectionId = productElement.dataset.section;

          const oldId = sectionId;
          const newId = `quickadd-${sectionId}`;
          productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
          Array.from(productElement.attributes).forEach((attribute) => {
            if (attribute.value.includes(oldId)) {
              productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
            }
          });

          productElement.dataset.originalSection = sectionId;
        }

        removeGalleryListSemantic(productElement) {
          const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
          if (!galleryList) return;

          galleryList.setAttribute('role', 'presentation');
          galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
        }

        updateImageSizes(productElement) {
          const product = productElement.querySelector('.product');
          const desktopColumns = product?.classList.contains('product--columns');
          if (!desktopColumns) return;

          const mediaImages = product.querySelectorAll('.product__media img');
          if (!mediaImages.length) return;

          let mediaImageSizes =
            '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

          if (product.classList.contains('product--medium')) {
            mediaImageSizes = mediaImageSizes.replace('715px', '605px');
          } else if (product.classList.contains('product--small')) {
            mediaImageSizes = mediaImageSizes.replace('715px', '495px');
          }

          mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
        }
      }
    );
    return true;
  }

  if (defineQuickAddModal()) return;

  /* Not defined yet — global.js has not run. DOMContentLoaded fires only after
     every deferred script has executed, so this is the last honest chance. */
  document.addEventListener('DOMContentLoaded', function () {
    if (defineQuickAddModal()) return;
    console.warn(
      '[quick-add] ModalDialog is undefined, so <quick-add-modal> could not be ' +
        'registered: global.js must load BEFORE quick-add.js. The choose-options ' +
        'popup on multi-variant product cards will not open. See layout/theme.liquid.'
    );
  });
})();